import { BillLineCandidate } from '../../interfaces/fixedAsset/fixedAssetValue.interface';
import { FixedAsset } from '../../interfaces/fixedAsset/fixedAsset.interface';

const pad = (n: number) => String(n).padStart(2, '0');
const firstOf = (y: number, m0: number) => {
  const d = new Date(y, m0, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

/** Default "depreciated through" for a carried-in asset: first day of the OPENING month (prior accumulated depreciation as of the opening date includes it). */
export const defaultDepreciatedThrough = (openingDate: string): string => {
  const [y, m] = openingDate.split('-').map(Number);
  if (!y || !m) return '';
  return firstOf(y, m - 1);
};

/** True when the depreciation start lies more than `months` months before the opening date. */
export const startLongBeforeOpening = (start: string, opening: string, months = 12): boolean => {
  const [sy, sm] = start.split('-').map(Number);
  const [oy, om] = opening.split('-').map(Number);
  if (!sy || !sm || !oy || !om) return false;
  return (oy * 12 + om) - (sy * 12 + sm) > months;
};

/** Run Depreciation: has the target month (any date inside it) already ended as of `today`? */
export const monthHasEnded = (target: string, today: string): boolean => {
  const [ty, tm] = target.split('-').map(Number);
  const [cy, cm] = today.split('-').map(Number);
  if (!ty || !tm || !cy || !cm) return true;
  return (cy * 12 + cm) > (ty * 12 + tm);
};

/** Last day of the month before the target month, as yyyy-mm-dd. */
export const previousMonthEnd = (target: string): string => {
  const [y, m] = target.split('-').map(Number);
  const d = new Date(y, m - 1, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Free-text category after the category link changes: on edit it always follows the link (as the server does); on create the user's own text wins. */
export const categoryTextAfterPick = (currentText: string, newCategoryName: string, isEdit: boolean): string =>
  isEdit ? newCategoryName : (currentText || newCategoryName);

/** Polish 18: every candidate line of the bill already is a fixed asset. */
export const allBillLinesRegistered = (lines: BillLineCandidate[]): boolean =>
  lines.length > 0 && lines.every(l => !!l.assetId);

/** Polish 19: gross cost the GL carries (cost plus revaluation adjustment). */
export const assetGrossCost = (a: Pick<FixedAsset, 'acquisitionCost' | 'grossCost'>): number =>
  a.grossCost ?? a.acquisitionCost;

/** Polish 19: GL accumulated depreciation plus impairment, so gross minus this equals the carrying amount. */
export const assetAccumulatedTotal = (
  a: Pick<FixedAsset, 'accumulatedDepreciation' | 'glAccumulatedDepreciation' | 'accumulatedImpairment'>): number =>
  (a.glAccumulatedDepreciation ?? a.accumulatedDepreciation) + (a.accumulatedImpairment ?? 0);

/** Polish 20: the month a depreciation row posted to: the shifted month if any, else its own period when a JE exists. */
export const postedToPeriod = (r: { period: string; postedToPeriod?: string; journalEntryId?: string }): string | undefined =>
  r.postedToPeriod ?? (r.journalEntryId ? r.period : undefined);

/** Register-existing: the life box holds the months LEFT, so a category's full life is never prefilled (and never counts as filled in); a new asset takes the category's life as its default. */
export const lifeAfterCategoryPick = (mode: string, current: string, categoryLife?: number | null): string =>
  mode === 'register' || categoryLife == null ? current : String(categoryLife);
export const lifeIsSet = (mode: string, remaining: number, categoryLife?: number | null): boolean =>
  remaining > 0 || (mode !== 'register' && (categoryLife ?? 0) > 0);
